import { AsyncPipe, NgClass, NgTemplateOutlet } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
  ViewChild,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { Marketplace } from "@front/m19-api-client";
import { PALETTE } from "@front/m19-metrics";
import { ProductEx } from "@front/m19-models";
import { AccountSelectionService, AsinService, getAmazonProductUrl, RankOption } from "@front/m19-services";
import {
  IButtonDirective,
  IIconComponent,
  IInputGroupDirective,
  IInputGroupInput,
  IInputGroupPrefixDirective,
  ISelectMenuImports,
  ISwitchButtonComponent,
  ITooltipDirective,
  ModalService,
  Option,
  ToasterService,
} from "@front/m19-ui";
import { Utils } from "@front/m19-utils";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { InputModalComponent, InputModalData } from "@m19-board/shared/input-modal/input-modal.component";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Observable } from "rxjs";
import { AsinQuickFilter, AsinSelectionFilter, KeywordTrackerService } from "../../../services/keyword-tracker.service";

@UntilDestroy()
@Component({
  selector: "app-tracked-asin-selection",
  templateUrl: "./tracked-asin-selection.component.html",
  styleUrls: ["./tracked-asin-selection.component.scss"],
  imports: [
    TranslocoDirective,
    IButtonDirective,
    AsyncPipe,
    NgClass,
    NgTemplateOutlet,
    ITooltipDirective,
    FormsModule,
    ISwitchButtonComponent,
    IInputGroupDirective,
    IInputGroupInput,
    IInputGroupPrefixDirective,
    IIconComponent,
    ISelectMenuImports,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TrackedAsinSelectionComponent implements OnInit {
  private asinService = inject(AsinService);
  private keywordTrackerService = inject(KeywordTrackerService);
  private translocoService = inject(TranslocoService);
  private readonly accountSelectionService = inject(AccountSelectionService);

  private readonly modalService = inject(ModalService);
  private readonly toastrService = inject(ToasterService);

  readonly palette = PALETTE;

  readonly childParentSwitchText = ["tracked-asin-selection.child", "tracked-asin-selection.parent"];
  readonly childParentSwitchValues = [false, true];

  @Input()
  marketplace: Marketplace | undefined;

  @Input() datavizScroll: HTMLDivElement | undefined;
  @Input() updateScrollEvent: Observable<Event> | undefined;

  @ViewChild("asinListScroll") asinListScroll: ElementRef | undefined;
  @ViewChild("addAsinInput") addAsinInput: ElementRef | undefined;

  @Output() hover = new EventEmitter<string>();

  @Output() leave = new EventEmitter<number>();

  @Output() deleted = new EventEmitter<string>();

  asinRanks$ = this.keywordTrackerService.asinRankRowData$;
  groupByParent$ = this.keywordTrackerService.groupByParent$;
  graphAsin$ = this.keywordTrackerService.graphAsin$;
  tableView$ = this.keywordTrackerService.tableView$;
  hiddenAsins$ = this.keywordTrackerService.hiddenAsins$;

  readonly isReadOnly = toSignal(this.accountSelectionService.readOnlyMode$);
  readonly selectedRankOption = toSignal(this.keywordTrackerService.rankOption$, { requireSync: true });
  selectedAsin?: string = "";

  searchAsinInput = signal<string>("");
  // init quick filter to user product
  quickFilterOptions: Option<AsinQuickFilter>[] = [
    {
      label: this.translocoService.translate("tracked-asin-selection.my_products"),
      value: AsinQuickFilter.MY_PRODUCT,
    },
    {
      label: this.translocoService.translate("tracked-asin-selection.competitors"),
      value: AsinQuickFilter.COMPETITORS,
    },
    {
      label: this.translocoService.translate("tracked-asin-selection.top_10"),
      value: AsinQuickFilter.TOP_10,
    },
  ];
  selectedQuickFilters = signal<Option<AsinQuickFilter>[]>([]);

  readonly products = signal(new Map<string, Observable<ProductEx>>());
  readonly productColor = toSignal(this.keywordTrackerService.asinColors$, { requireSync: true });

  ngOnInit(): void {
    this.setupFilter();

    this.keywordTrackerService.graphAsin$.pipe(untilDestroyed(this)).subscribe((a) => (this.selectedAsin = a));

    this.asinRanks$.pipe(untilDestroyed(this)).subscribe((asins) => {
      const products = new Map(this.products());
      for (const asin of asins) {
        this.addAsinToSet(products, asin.asin);

        if (asin.childrenRow) {
          for (const c of asin.childrenRow) {
            this.addAsinToSet(products, c.asin);
          }
        }
      }
      this.products.set(products);
    });

    this.updateScrollEvent?.pipe(untilDestroyed(this)).subscribe((e: Event) => {
      this.updateScroll("datavizScroll", e);
    });
  }

  setupFilter() {
    const LsQuickFilter: AsinQuickFilter[] = JSON.parse(localStorage.getItem("KeywordTrackerFilter") || "[]");

    this.selectedQuickFilters.set(
      LsQuickFilter && LsQuickFilter.length
        ? this.quickFilterOptions.filter((q) => LsQuickFilter.includes(q.value))
        : [this.quickFilterOptions[0]],
    );

    this.updateQuickFilter(this.selectedQuickFilters());
  }

  hideAsin(event: Event, asin: string) {
    event.stopPropagation();
    this.keywordTrackerService.toggleHiddenAsins(asin);
  }

  private addAsinToSet(products: Map<string, Observable<ProductEx>>, asin: string) {
    if (products.has(asin)) {
      return;
    }
    products.set(asin, this.asinService.getProductWithMarketplace(asin, this.marketplace!));
  }

  switchGroupBy(groupByParent: boolean) {
    this.keywordTrackerService.setGroupByParent(groupByParent);
  }

  updateScroll(id: string, event: Event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.datavizScroll && id === "asinListScroll") {
      this.datavizScroll.scrollTop = this.asinListScroll?.nativeElement.scrollTop || 0;
    } else if (this.asinListScroll && id === "datavizScroll") {
      this.asinListScroll.nativeElement.scrollTop = this.datavizScroll?.scrollTop || 0;
    }
  }

  toggleGraphAsin(asin: string) {
    // prevent deselection in both option
    if (this.selectedRankOption() === RankOption.BOTH && this.selectedAsin === asin) return;

    this.keywordTrackerService.toggleGraphAsin(asin);
  }

  updateQuickFilter(options: Option<AsinQuickFilter>[] | undefined) {
    this.selectedQuickFilters.set(options ?? []);
    const newFilter: AsinSelectionFilter = {
      quickFilter: new Set(this.selectedQuickFilters().map((q) => q.value)),
      searchAsin: this.searchAsinInput(),
    };
    this.keywordTrackerService.setAsinSelectionFilter(newFilter);
  }

  searchAsin(asin: string) {
    this.searchAsinInput.set(asin);
    const newFilter: AsinSelectionFilter = {
      quickFilter: new Set(this.selectedQuickFilters().map((q) => q.value)),
      searchAsin: asin,
    };
    this.keywordTrackerService.setAsinSelectionFilter(newFilter);
  }

  onHover(asin: string) {
    this.hover.emit(asin);
  }

  onLeave() {
    this.leave.emit();
  }

  openInputModal() {
    const ref = this.modalService.openModal<InputModalData, string>(InputModalComponent, {
      modalTitle: this.translocoService.translate("tracked-asin-selection.add_product"),
      data: {
        initialValue: "",
        maxLength: 200,
      },
    });
    ref.afterClosed().subscribe((value: string) => {
      if (value) {
        if (!Utils.isValidAsin(value)) {
          this.toastrService.error(this.translocoService.translate("common.invalid_asin"));
          return;
        }
        this.keywordTrackerService.trackNewProduct(value);
        this.toastrService.success(this.translocoService.translate("keyword-tracker.new_products_tracked"));
      }
    });
  }

  protected readonly getAmazonProductUrl = getAmazonProductUrl;
  protected readonly PALETTE = PALETTE;
  protected readonly RankOption = RankOption;
}
